import React, { ComponentType, PropsWithChildren, useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { BoxProps } from '../box';
import { FormControlProps, FormFieldSize } from './model';
import { FieldLabel } from './label';
import { DEFAULT_FIELD_SIZE, name } from './const';
import { Root } from './fieldRoot';

export interface FormFieldProps extends PropsWithChildren, Omit<BoxProps, 'id' | 'theme'> {
  name: string;
  label?: React.ReactNode;
  placeholder?: string;
  size?: FormFieldSize;
  rootRef?: React.Ref<HTMLDivElement>;
}

export interface FormFieldWithControl<V = string, E extends HTMLElement = HTMLInputElement> extends FormFieldProps {
  Control: ComponentType<FormControlProps<V, E>>;
}
export interface FormFieldWithRender<V = string, E extends HTMLElement = HTMLInputElement> extends FormFieldProps {
  render: (props: FormControlProps<V, E>) => JSX.Element;
}

export const FormField = <E extends HTMLElement = HTMLInputElement>({
  size = DEFAULT_FIELD_SIZE,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  name,
  label,
  rootRef,
  children,
  placeholder,
  tabIndex,
  ...props
}: FormFieldWithControl<any, E> | FormFieldWithRender<any, E>) => {
  const id = useId();
  const { control, formState, getFieldState } = useFormContext();
  const { field } = useController({ name, control });
  const { invalid } = getFieldState(name);
  const Control = 'Control' in props ? props.Control : () => null;
  const render = 'render' in props ? props.render : () => null;

  return (
    <Root className={clsx(size)} ref={rootRef} sx={props.sx as any}>
      {label && (
        <FieldLabel htmlFor={id} size={size}>
          {label}
        </FieldLabel>
      )}
      <Control
        {...field}
        disabled={formState.isSubmitting}
        id={id}
        invalid={invalid}
        onChange={field.onChange}
        placeholder={placeholder}
        size={size}
        tabIndex={tabIndex}
      />
      {render({ ...field, onChange: field.onChange, placeholder, disabled: formState.isSubmitting, invalid, id, tabIndex, size })}
      {children}
    </Root>
  );
};
FormField.displayName = name;
